import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMembers } from '@/features/members/public';
import { FloatingLabelInput } from '@/shared/components/custom/form-field/FloatingLabelInput';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { MemberCycleActions } from './MemberCycleActions';

/**
 * Searchable member list for the member-entitlement actions
 * (`MemberCycleActions` — hire date, cycle-open, balance adjustment, unified
 * in one "···" menu) — these mutate a specific member's state. Lives under
 * the "Equipo" tab's "Miembros" sub-tab, Owner/Admin only.
 */
export function MembersTab() {
  const { t } = useTranslation('timeOff');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, isError } = useMembers(debouncedSearch);

  return (
    <div className="space-y-4" data-testid="time-off-settings-members-tab">
      <p className="text-sm text-muted-foreground">{t('team.members.title')}</p>

      <div className="max-w-sm">
        <FloatingLabelInput
          id="time-off-members-search"
          label={t('team.members.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          hasValue={!!search}
          data-testid="time-off-members-search"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2" data-testid="time-off-settings-members-loading">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground" data-testid="time-off-settings-members-error">
          {t('team.members.loadError')}
        </p>
      ) : data.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="time-off-settings-members-empty">
          {t('team.members.empty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {data.map((member) => (
            <li
              key={member.userId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
              data-testid={`time-off-settings-member-row-${member.userId}`}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{member.displayName}</p>
                <p className="truncate text-xs text-muted-foreground">{member.email}</p>
              </div>
              <MemberCycleActions userId={member.userId} displayName={member.displayName} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
